/**
 * Interior ELEVATION MARKS (ADR 0055, plan §5/4b — the Designer Portal repo's
 * docs/PRICING_AND_INTERIORS_PLAN.md).
 *
 * Turns a SOLVED interior (src/geometry/interior.js, ADR 0054) into the flat
 * list of drawing marks the front elevation renders inside one placed unit:
 * rod lines with end brackets, adjustable/fixed shelf lines, drawer faces with
 * pulls, shoe-shelf lines (flat or slant-symbol), and the door face drawn over
 * everything. Pure — no DOM — so the mark geometry is node-testable the way
 * polygonTopCentre is; src/ui/elevation.js maps each mark to SVG.
 *
 * Coordinates: `x` runs 0 → widthUnits across the unit's opening (the caller
 * offsets to the wall strip); every vertical figure is an ELEVATION off the
 * floor in units, like the solver's output and every other height in the app.
 *
 * The marks deliberately mirror how the CV elevation PDFs draw these things
 * (the visual acceptance reference, owner 2026-08-08):
 *   - a rod is a line at the top of its hang zone with short drop brackets,
 *     plus a HANGER glyph hanging from it (ADR 0060) so a rod cannot be
 *     mistaken for a shelf at a glance;
 *   - an interior shelf is a BOARD at its real thickness — ¾", or 1½" when it
 *     is tagged double-thick (owner, 2026-08-09: "show shelves as 3/4" thick
 *     (currently very thin lines)… as that mirrors actual size"). Fixed and
 *     adjustable are told apart by FILL, not by thickness: a fixed board is
 *     solid, an adjustable one is an outline (ADR 0062 §8);
 *   - a drawer face is its real solved rectangle — drawn PROUD of its opening
 *     by the ¼" side overlay (ADR 0064) — carrying its HANDLES, centred both
 *     ways, or at the quarter points when the face is wide enough to need two;
 *   - shoe shelves are one thin shelf per solved pitch; the SSS slant is a
 *     SYMBOL (each line tilted) — a true front view cannot show the tilt, so
 *     the tilt is notation, exactly as CV's double-edge band is;
 *   - a door is drawn LAST, outline-only, with a pull dot at its latch
 *     side, so the interior behind it stays fully readable — as a left/right
 *     PAIR of leaves once the opening is wide enough to be ordered as one
 *     (ADR 0060, superseding ADR 0055's single face).
 */

import { deriveDoorGeometry, doorLeaves, doorStyleByKey } from '../geometry/doors.js';
import { drawerFaceWidthUnits, handleLengthUnits, handleRules, handlesForFace, handleType } from '../geometry/handles.js';
import { isSpaceKind } from '../geometry/interior.js';

// The leaf geometry moved to src/geometry/doors.js so the purchasing take-off
// can count exactly the leaves this module draws (ADR 0068). Re-exported
// because this is where every caller has looked for it.
export { doorLeaves };

/** Rod brackets / door pulls sit this far in from the opening's edges. */
const EDGE_INSET_UNITS = 1.5;
/** A hang rod's real diameter (owner, 2026-08-09: "rods at 1 1/4" thick as
 *  that mirrors actual size"). The default only; the app passes the
 *  `hang_rod_diameter_units` rule. ADR 0062 §8. */
const ROD_DIAMETER_UNITS = 1.25;
/** An SSS shoe shelf's slant symbol rises at most this much across the span. */
const SHOE_SLANT_MAX_RISE_UNITS = 2.5;
/** The HANGER glyph (owner, 2026-08-09: "show a hanger hanging from the rod so
 *  people know it is a hanging rod v. a shelf"). Nominal figures for a real
 *  wire hanger, scaled down on a narrow opening and skipped when it would be
 *  an unreadable smudge. */
const HANGER_HOOK_CREST_UNITS = 0.9;  // how far the hook crests ABOVE the rod body
const HANGER_SHOULDER_DROP_UNITS = 3.6; // shoulders hang this far BELOW the rod
const HANGER_HALF_WIDTH_UNITS = 3.6;  // half the shoulder span
/** Below these the hanger is dropped: it would read as a blot, not a hanger. */
const HANGER_MIN_WIDTH_UNITS = 9;
const HANGER_MIN_ZONE_UNITS = 6;

const EPS = 0.01;

/** Edge inset that still leaves a line on a very narrow opening. */
function inset(widthUnits) {
  return Math.min(EDGE_INSET_UNITS, widthUnits * 0.08);
}

/** A shelf group solved by an OLD result (no `shelfLines`): the ADR 0060 pitch
 *  convention, so a stale feed still draws instead of drawing nothing. */
function evenLinesFallback(el) {
  const count = Number.isInteger(el.count) ? el.count : 0;
  if (!(count > 0) || !(el.pitchUnits > EPS)) return [];
  return Array.from({ length: count }, (_, k) => el.fromUnits + el.pitchUnits * k);
}

/**
 * DIMENSION-DISPLAY thresholds (ADR 0061 item E). The owner asked to toggle
 * shelf-spacing and drawer-face figures on the elevation instead of opening
 * each item. Same discipline as the hanger glyph: a label that cannot be read
 * says LESS than no label, so one that will not fit is skipped rather than
 * drawn over its neighbour.
 */
const DIM_MIN_OPENING_WIDTH_UNITS = 6;
const DIM_MIN_SPAN_UNITS = 3;
/** Dimension figures sit this fraction across the opening, off the pulls. */
const DIM_X_FRACTION = 0.26;

/**
 * The vertical band a door face covers (ADR 0060, superseding ADR 0055's v1
 * halves convention):
 *
 *   - `full` runs from the top of the clear band DOWN TO THE TOP OF THE
 *     TOPMOST DRAWER BANK, when the interior has one (owner, 2026-08-09:
 *     "Full would put doors from the top of the section to top of the
 *     drawers"), else the whole band. Drawer faces are the finished front —
 *     a door over them is not a thing that gets built.
 *   - `lower`/`upper` take the designer's `heightUnits` off the bottom/top of
 *     the band. `null` keeps ADR 0055's half, so every sketch stored before
 *     this change draws exactly what it drew. A height taller than the band is
 *     CLAMPED to it and flagged — same policy as an overflowing element: draw
 *     the truth, badge the problem, never a negative box.
 *
 * @param {{coverage:'full'|'lower'|'upper', heightUnits?:number|null}} doors
 * @param {{bottomUnits:number, topUnits:number}} band
 * @param {{solved?:{elements?:Array<object>}}} [feed] the SOLVED interior, so
 *   `full` can find the drawers. Omitted → no drawer datum, whole band.
 * @returns {{fromUnits:number, toUnits:number, clamped:boolean}|null}
 */
export function doorBand(doors, band, feed = {}) {
  const bands = doorBands(doors, band, feed);
  if (!bands.length) return null;
  // The whole extent the doors cover, so one caller (the editor's warning
  // line) can ask "did anything clamp?" whatever the coverage is.
  return {
    fromUnits: Math.min(...bands.map((b) => b.fromUnits)),
    toUnits: Math.max(...bands.map((b) => b.toUnits)),
    clamped: bands.some((b) => b.clamped),
    ...(bands.length > 1 ? { splitAtUnits: bands[0].toUnits } : {}),
  };
}

/**
 * The door band(s) a section's doors cover — one for `full`/`lower`/`upper`,
 * TWO for `split` (ADR 0060 item 3): a lower door and an upper door meeting at
 * `splitAtUnits`. The two are complementary by construction — the upper starts
 * exactly where the lower ends — because they are derived from one number.
 *
 * @returns {Array<{part:'full'|'lower'|'upper', fromUnits:number, toUnits:number, clamped:boolean}>}
 */
export function doorBands(doors, band, feed = {}) {
  if (!doors || !band || !(band.topUnits - band.bottomUnits > EPS)) return [];
  const bottom = band.bottomUnits;
  const top = band.topUnits;
  if (doors.coverage === 'full' || doors.coverage === 'split') {
    // Both run the section's full door EXTENT: top of the band down to the top
    // of the drawers (item 2). A split then divides that extent in two.
    const from = topmostDrawerTop(feed, bottom, top);
    if (doors.coverage === 'full') return [{ part: 'full', fromUnits: from, toUnits: top, clamped: false }];
    const wanted = doors.splitAtUnits > 0 ? doors.splitAtUnits : (from + top) / 2;
    const at = Math.min(Math.max(wanted, from), top);
    const clamped = Math.abs(at - wanted) > EPS;
    return [
      { part: 'lower', fromUnits: from, toUnits: at, clamped },
      { part: 'upper', fromUnits: at, toUnits: top, clamped },
    ];
  }
  const span = top - bottom;
  const wanted = doors.heightUnits > 0 ? doors.heightUnits : span / 2;
  const height = Math.min(wanted, span);
  const clamped = wanted > span + EPS;
  return doors.coverage === 'upper'
    ? [{ part: 'upper', fromUnits: top - height, toUnits: top, clamped }]
    : [{ part: 'lower', fromUnits: bottom, toUnits: bottom + height, clamped }];
}

/** The TOP of the highest solved drawer bank, or the band bottom when none. */
function topmostDrawerTop(feed, bottom, top) {
  let best = bottom;
  for (const el of feed?.solved?.elements ?? []) {
    if (el?.kind !== 'drawerBank' || !(el.drawers?.length > 0)) continue;
    if (!(el.toUnits - el.fromUnits > EPS)) continue; // clamped to nothing
    if (el.toUnits > best) best = el.toUnits;
  }
  return Math.min(Math.max(best, bottom), top);
}

/**
 * The CLEAR GAPS between the boards inside one unit — what a designer means by
 * "shelf spacing" (ADR 0061 item E, owner: "toggle on/off the display of
 * spacing between shelves … v. requiring designers to open and click on each
 * item, which is how it works in CV").
 *
 * The boards are the composed shelves (single and grouped) plus any drawer
 * BANK, which is a solid block you measure to rather than through, plus the
 * band's own bottom and top decks. Shoe shelves are deliberately excluded: a
 * shoe rack is one element at one stated pitch, and dimensioning every slot
 * would bury the drawing in figures that say the same thing.
 *
 * @returns {Array<{fromUnits:number, toUnits:number, spanUnits:number}>}
 *   ascending, smallest-first-gap included; sub-threshold gaps are dropped.
 */
export function shelfSpacingGaps(feed) {
  const band = feed?.band;
  if (!band || !(band.topUnits - band.bottomUnits > EPS)) return [];
  const boards = [
    { fromUnits: band.bottomUnits, toUnits: band.bottomUnits },
    { fromUnits: band.topUnits, toUnits: band.topUnits },
  ];
  for (const el of feed.solved?.elements ?? []) {
    if (el?.kind === 'shelf' && el.omitted !== true) {
      boards.push({ fromUnits: el.fromUnits, toUnits: el.toUnits });
    } else if (el?.kind === 'shelfGroup') {
      const t = el.thicknessUnits > 0 ? el.thicknessUnits : 0.75;
      const lines = Array.isArray(el.shelfLines) && el.shelfLines.length ? el.shelfLines : evenLinesFallback(el);
      for (const at of lines) {
        if (Number.isFinite(at)) boards.push({ fromUnits: at - t, toUnits: at });
      }
    } else if (el?.kind === 'shoeShelves') {
      // Shoe shelves are SHELVES and their spacing is exactly what a designer
      // wants to read (owner, 2026-08-09: the figures were missing on a shoe
      // section even with the toggle on). Each solved line is a board, at the
      // same pitch the marks draw — `atUnits` is a line's TOP, matching the
      // shelf case, so the gaps come out as the clear space between them.
      const count = Number.isInteger(el.count) ? el.count : 0;
      const t = el.thicknessUnits > 0 ? el.thicknessUnits : 0.75;
      if (count > 0 && el.pitchUnits > EPS) {
        for (let k = 0; k < count; k += 1) {
          const at = el.fromUnits + el.pitchUnits * k;
          boards.push({ fromUnits: at - t, toUnits: at });
        }
      }
    } else if (el?.kind === 'drawerBank' && el.drawers?.length > 0 && el.toUnits - el.fromUnits > EPS) {
      boards.push({ fromUnits: el.fromUnits, toUnits: el.toUnits });
    }
  }
  boards.sort((a, b) => a.fromUnits - b.fromUnits);
  const gaps = [];
  for (let i = 0; i < boards.length - 1; i += 1) {
    const fromUnits = boards[i].toUnits;
    const toUnits = boards[i + 1].fromUnits;
    const spanUnits = toUnits - fromUnits;
    if (spanUnits >= DIM_MIN_SPAN_UNITS) gaps.push({ fromUnits, toUnits, spanUnits });
  }
  return gaps;
}

/**
 * Every drawer FACE height in the unit (ADR 0061 item E) — the other figure the
 * owner asked to see without opening each item.
 *
 * @returns {Array<{fromUnits:number, toUnits:number, spanUnits:number}>}
 */
export function drawerFaceHeights(feed) {
  const out = [];
  for (const el of feed?.solved?.elements ?? []) {
    if (el?.kind !== 'drawerBank') continue;
    for (const d of el.drawers ?? []) {
      const spanUnits = d.toUnits - d.fromUnits;
      if (spanUnits > EPS) out.push({ fromUnits: d.fromUnits, toUnits: d.toUnits, spanUnits });
    }
  }
  return out;
}

/**
 * Flattens one unit's solved interior feed into drawing marks.
 *
 * @param {{solved?:object, doors?:object|null, band?:object}|null} feed what
 *   the model's `interiorForUnit()` returns: `solved` = solveInterior()'s
 *   result, `doors` = the section's normalized doors, `band` = the clear
 *   interior band the interior was solved in (interiorBand()).
 * @param {number} widthUnits the unit's horizontal span (opening or own width)
 * @param {object} [opts] door-pair rules: pairCenterRevealUnits /
 *   pairMinWidthUnits (the `door_pair_*` business rules).
 * @returns {Array<object>} marks in render order (door face LAST so it draws
 *   over the interior). Kinds:
 *   - {kind:'rod', fromX, toX, atUnits, diameterUnits, warn}  atUnits = the
 *      rod body's TOP edge; it hangs `diameterUnits` below it
 *   - {kind:'hanger', x, atUnits, hookRiseUnits, dropUnits, halfWidthUnits, warn}
 *   - {kind:'shelf', elementIndex, fromX, toX, atUnits, fixed, warn}
 *      atUnits = top surface. `elementIndex` addresses the element the mark
 *      came from — a number for a DISCRETE shelf, and NULL for a shelfGroup
 *      member, which has no per-board element to write to.
 *   - {kind:'drawer', fromX, toX, fromUnits, toUnits, warn, handles:[...]}
 *      fromX/toX span the FACE, which overhangs the opening by the ¼" side
 *      overlay at each end (so fromX is negative). Each handle is
 *      {x, atUnits, lengthUnits, fromX, toX, type, mounting, draw} — `x` is the
 *      centre, `atUnits` the centreline elevation, and `draw` says whether the
 *      renderer puts a bar of `lengthUnits` there or a knob dot (ADR 0064).
 *   - {kind:'shoeShelf', fromX, toX, atUnits, riseUnits, warn}  riseUnits > 0
 *      tilts the line (SSS slant symbol); 0 is flat (FSS)
 *   - {kind:'door', side, fromX, toX, fromUnits, toUnits, warn, pull:{x, atUnits}}
 *   - {kind:'spacingDim', x, fromUnits, toUnits, spanUnits}  (ADR 0061 item E,
 *      only when opts.showShelfSpacing)
 *   - {kind:'faceDim', x, fromUnits, toUnits, spanUnits}     (only when
 *      opts.showDrawerFaceHeights)
 *   - {kind:'spaceRegion', elementIndex, fromX, toX, fromUnits, toUnits}
 *      (ADR 0067, only when opts.showSpaceRegions — the editor's row↔picture
 *      highlight target; invisible until a row is focused)
 */
export function interiorMarks(feed, widthUnits, opts = {}) {
  const w = widthUnits > 0 ? widthUnits : 0;
  if (!feed || !(w > 0)) return [];
  const marks = [];
  const rodDiameter = opts.hangRodDiameterUnits > 0 ? opts.hangRodDiameterUnits : ROD_DIAMETER_UNITS;

  // SPACE REGIONS (ADR 0067) — one invisible rectangle per SPACE, carrying its
  // element index, emitted ONLY for the interior editor's preview
  // (`showSpaceRegions`). It is what lets the editor light up the region a
  // space row is talking about: the strongest possible answer to "which row is
  // which picture", and it costs nothing anywhere else because no other
  // elevation asks for it. Drawn FIRST so it sits behind every real mark.
  if (opts.showSpaceRegions === true) {
    (feed.solved?.elements ?? []).forEach((el, elementIndex) => {
      if (!el || !isSpaceKind(el.kind)) return;
      if (!(el.toUnits - el.fromUnits > EPS)) return;
      marks.push({
        kind: 'spaceRegion', elementIndex, fromX: 0, toX: w,
        fromUnits: el.fromUnits, toUnits: el.toUnits,
      });
    });
  }

  for (const [elementIndex, el] of (feed.solved?.elements ?? []).entries()) {
    if (el?.omitted === true) continue;
    // Overflowed or pin-ignored elements still draw — at their clamped spans —
    // but flagged, so the drawing shows the problem instead of hiding it
    // (ADR 0054: never a negative box, never a silent move).
    const warn = el.overflow === true || el.pinIgnored === true;
    if (el.kind === 'rod') {
      const zone = el.toUnits - el.fromUnits;
      const rodBodyUnits = Math.min(rodDiameter, Math.max(zone, 0));
      marks.push({
        kind: 'rod',
        // FULL WIDTH of the opening (owner, 2026-08-09: "rods should be the
        // full width of the openings, right now they are displayed with gaps
        // on both sides"). A rod really does run divider to divider; the old
        // 1.5" inset existed to hang the end brackets on, and the brackets
        // existed to stop a 1px line reading as a shelf — a job the real 1 1/4"
        // body and the hanger glyph now do (ADR 0062 §8).
        fromX: 0,
        toX: w,
        atUnits: el.rodAtUnits,
        // The rod is a BODY, not a line: `atUnits` is its TOP edge — the datum
        // the two-hole drop is measured to (ADR 0062 §7) — and it hangs its
        // own diameter below that. Squeezed to a sliver of hang zone it never
        // draws taller than the space the composition actually left it.
        diameterUnits: rodBodyUnits,
        warn,
      });
      // A HANGER hanging from the rod, so a rod reads apart from a shelf at a
      // glance (owner, 2026-08-09). Skipped on an opening too narrow or a hang
      // zone too short for it to be legible — a smudge would say LESS than the
      // bare rod line does.
      if (w >= HANGER_MIN_WIDTH_UNITS && zone >= HANGER_MIN_ZONE_UNITS) {
        const halfWidth = Math.min(HANGER_HALF_WIDTH_UNITS, w * 0.22);
        // The hanger hangs from the ROD, not from the board above it (owner,
        // 2026-08-09). When the rod became a real 1 1/4" BODY (ADR 0062 §8)
        // this glyph kept the old line-rod datum: its hook rose from the rod's
        // TOP, which — now that the top sits two holes BELOW the fixed board —
        // reached up toward that board and read as hanging off the shelf. The
        // hook now crests just above the rod and the shoulders hang from the
        // rod's UNDERSIDE, so it visibly wraps the rod it hangs on.
        const rodBottom = el.rodAtUnits - rodBodyUnits;
        marks.push({
          kind: 'hanger',
          x: w / 2,
          atUnits: rodBottom,
          hookRiseUnits: rodBodyUnits + HANGER_HOOK_CREST_UNITS,
          dropUnits: Math.min(HANGER_SHOULDER_DROP_UNITS, Math.max(zone - rodBodyUnits, 0) * 0.6),
          halfWidthUnits: halfWidth,
          warn,
        });
      }
    } else if (el.kind === 'shelf') {
      marks.push({
        kind: 'shelf',
        // A DISCRETE shelf is addressable: this index is the one
        // draftSetShelfPin() takes, so a drag can write back to the element it
        // came from. A group member cannot (see the shelfGroup branch below).
        elementIndex,
        fromX: 0,
        toX: w,
        atUnits: el.toUnits,
        fixed: el.fixed === true,
        // LOCKED (owner, 2026-08-30). Carried on the mark for two reasons: the
        // renderer draws a padlock so the state is visible on the drawing
        // rather than only in a list, and shelfGrabBands() withholds the drag
        // target so the pointer falls through to the pan it would otherwise
        // have swallowed.
        locked: el.locked === true,
        // A DOUBLE-THICK shelf draws as ONE board of its real thickness, never
        // two lines (ADR 0061 item C — the whole point of the tag).
        doubleThick: el.doubleThick === true,
        thicknessUnits: el.thicknessUnits > 0 ? el.thicknessUnits : el.toUnits - el.fromUnits,
        warn,
      });
    } else if (el.kind === 'shelfGroup') {
      // The count-based group draws as ordinary shelf lines at the positions
      // the SOLVER decided (`shelfLines`) — even pitch, then snapped onto
      // system holes (ADR 0061). Re-deriving them here would let the drawing
      // and the model disagree about where the boards are.
      const lines = Array.isArray(el.shelfLines) && el.shelfLines.length
        ? el.shelfLines
        : evenLinesFallback(el);
      const t = el.thicknessUnits > 0 ? el.thicknessUnits : 0.75;
      for (const atUnits of lines) {
        if (!Number.isFinite(atUnits)) continue;
        marks.push({
          kind: 'shelf',
          // NULL, and deliberately so — a positive assertion, not an omission.
          // A group is ONE element carrying a COUNT; its members are solver
          // output with no `atUnits` of their own, so there is nothing a
          // per-member drag could write. The designer's route to individually
          // placeable boards is draftInsertShelvesIntoOpening(), which
          // materializes discrete pinned shelves (interior-editor.js). Ruling 3
          // (owner, 2026-08-29) says a drag moves ONE shelf; a group member is
          // therefore not a drag target at all — no handle, and no refusal
          // message either.
          elementIndex: null,
          fromX: 0,
          toX: w,
          atUnits,
          fixed: el.fixed === true,
          doubleThick: el.doubleThick === true,
          thicknessUnits: t,
          warn,
        });
      }
    } else if (el.kind === 'drawerBank') {
      // A drawer FACE is WIDER than the opening it covers: it overlays the
      // side panels by ¼" at each end (owner, 2026-08-09 — "the section width
      // plus 1/2" due to the 1/4" overlay on the left and right side panels"),
      // so it draws PROUD of the opening rather than flush inside it. That is
      // both a rendering correction and the number the two-pull rule measures
      // against — ADR 0064.
      const overlay = handleRules(opts).faceSideOverlayUnits;
      const faceW = drawerFaceWidthUnits(w, opts);
      for (const d of el.drawers ?? []) {
        const face = d.toUnits - d.fromUnits;
        if (!(face > EPS)) continue; // clamped to nothing by overflow
        // HANDLES from the one shared rule (src/geometry/handles.js): centred
        // both ways, or on the top edge for a tab pull, and ONE or TWO of them
        // by the face's real width. The take-off counts the same call, so what
        // is drawn and what is purchased can never diverge.
        const handles = handlesForFace(
          { widthUnits: faceW, heightUnits: face, type: el.handleType },
          opts,
        ).map((h) => {
          // Face-local → opening-local: the face starts `overlay` LEFT of the
          // opening's zero, so every handle x shifts by that much.
          const x = h.x - overlay;
          return {
            x,
            // `centerFromTopUnits` is measured DOWN from the face top; the
            // drawing's axis is an elevation off the floor, so it subtracts.
            atUnits: d.toUnits - h.centerFromTopUnits,
            lengthUnits: h.lengthUnits,
            fromX: x - h.lengthUnits / 2,
            toX: x + h.lengthUnits / 2,
            type: h.type,
            mounting: h.mounting,
            draw: h.draw,
          };
        });
        marks.push({
          kind: 'drawer',
          fromX: -overlay,
          toX: w + overlay,
          fromUnits: d.fromUnits,
          toUnits: d.toUnits,
          warn,
          handles,
        });
      }
    } else if (el.kind === 'shoeShelves') {
      const count = Number.isInteger(el.count) ? el.count : 0;
      if (count > 0 && el.pitchUnits > EPS) {
        const rise = el.style === 'SSS'
          ? Math.min(el.pitchUnits * 0.5, SHOE_SLANT_MAX_RISE_UNITS)
          : 0;
        for (let k = 0; k < count; k += 1) {
          marks.push({
            kind: 'shoeShelf',
            fromX: 0,
            toX: w,
            // One shelf at the bottom of each pitch slot, lowest at the
            // element's solved bottom — evenly pitched, the solver's math.
            atUnits: el.fromUnits + el.pitchUnits * k,
            riseUnits: rise,
            warn,
          });
        }
      }
    }
    // openSpace: nothing, by definition.
  }

  // The doors LAST, over the interior they cover — each band as a left/right
  // PAIR of leaves with a centre reveal, or one leaf on a narrow opening
  // (ADR 0060 item 2). A `split` contributes two bands plus their meeting line.
  const extentDoors = Boolean(feed.doors?.fromElementId && feed.doors?.toElementId);
  const style = doorStyleByKey(opts.doorStyles, feed.doors?.styleKey);
  const derived = extentDoors
    ? deriveDoorGeometry(feed.doors, feed.solved?.elements, w, style, opts)
    : null;
  const bands = derived?.ok ? derived.bands : (extentDoors ? [] : doorBands(feed.doors, feed.band, feed));
  const leaves = derived?.ok ? derived.leaves : doorLeaves(w, opts);
  for (const span of bands) {
    if (!(span.toUnits - span.fromUnits > EPS)) continue;
    const selectedHandle = handleType(feed.doors?.handleType, opts);
    const handleLength = handleLengthUnits(selectedHandle.key, opts);
    const atUnits = selectedHandle.mounting === 'topEdge'
      ? span.toUnits : (span.fromUnits + span.toUnits) / 2;
    for (const leaf of leaves) {
      marks.push({
        kind: 'door',
        part: span.part,
        side: leaf.side,
        fromX: leaf.fromX,
        toX: leaf.toX,
        fromUnits: span.fromUnits,
        toUnits: span.toUnits,
        // A door height the band could not hold is badged like any overflow.
        warn: span.clamped === true,
        pull: {
          x: leaf.pullX,
          atUnits,
          draw: selectedHandle.draw,
          mounting: selectedHandle.mounting,
          lengthUnits: handleLength,
          type: selectedHandle.key,
        },
      });
    }
  }
  if (bands.length > 1) {
    // One meeting edge per derived height boundary. Legacy split records have
    // two bands and therefore still emit exactly one edge.
    for (const band of bands.slice(0, -1)) {
      marks.push({ kind: 'doorSplit', fromX: 0, toX: w, atUnits: band.toUnits, warn: band.clamped === true });
    }
  }

  // DIMENSION marks LAST of all, over everything (ADR 0061 item E), each
  // behind its own independent toggle. On an opening too narrow to carry a
  // figure they are skipped wholesale rather than drawn as a smudge — the
  // hanger's discipline. The renderer formats the number; this module only
  // says WHERE a figure goes and HOW BIG the thing it measures is.
  if (w >= DIM_MIN_OPENING_WIDTH_UNITS) {
    if (opts.showShelfSpacing === true) {
      for (const gap of shelfSpacingGaps(feed)) {
        marks.push({ kind: 'spacingDim', x: w * DIM_X_FRACTION, ...gap, warn: false });
      }
    }
    if (opts.showDrawerFaceHeights === true) {
      for (const face of drawerFaceHeights(feed)) {
        marks.push({ kind: 'faceDim', x: w * (1 - DIM_X_FRACTION), ...face, warn: false });
      }
    }
  }
  return marks;
}

/**
 * Every drawn BOARD, bottom-up — the shared basis for both the grab targets and
 * the drag bounds, so a board that blocks a drag is exactly a board that blocks
 * a target. Two lists would be two chances to disagree about where the wood is.
 *
 * A group member appears here with `elementIndex: null`: it is a real board in
 * the way, so it constrains its neighbours, but it is not itself addressable.
 */
function boardsFrom(marks) {
  return (Array.isArray(marks) ? marks : [])
    .filter((m) => m?.kind === 'shelf' && Number.isFinite(m.atUnits))
    .map((m) => ({
      elementIndex: Number.isInteger(m.elementIndex) ? m.elementIndex : null,
      locked: m.locked === true,
      top: m.atUnits,
      bottom: m.atUnits - (m.thicknessUnits > 0 ? m.thicknessUnits : 0),
    }))
    .sort((a, b) => a.bottom - b.bottom);
}

/**
 * WCAG 2.2 SC 2.5.8 (Target Size (Minimum), Level AA) — 24 CSS px. A ¾" board
 * draws about 2 px tall at the wall elevation's default zoom and only a little
 * more in the editor preview, so the thing you GRAB has to be taller than the
 * thing you SEE. This is the floor it grows to.
 */
export const SHELF_GRAB_MIN_PX = 24;

/**
 * The hittable band for each DRAGGABLE shelf mark — taller than the board, but
 * never tall enough to reach its neighbour.
 *
 * Two rules, and the second one is the point:
 *
 *  1. Grow the board symmetrically until it is at least `minPx` tall on screen.
 *  2. NEVER grow past the MIDPOINT between this board and the next one, in
 *     either direction. Shelves may legally sit ONE system hole apart (owner
 *     ruling 2, 2026-08-29 — 32 mm), which at a working zoom is far less than
 *     24 px, so unclamped targets would overlap and a drag aimed at one shelf
 *     would silently pick up its neighbour. A target that MISSES is a retry; a
 *     target that grabs the WRONG BOARD moves something the designer never
 *     touched. The midpoint keeps every band disjoint by construction.
 *
 * Group members bound the growth of their neighbours (they are real boards in
 * the way) but get no band of their own — `elementIndex: null` is not a drag
 * target. See interiorMarks()'s shelfGroup branch for why.
 *
 * Pure — takes marks and a scale, touches no DOM — so the clamping rule is
 * node-testable rather than something you have to open a browser to check.
 *
 * @param {Array<object>} marks the output of interiorMarks()
 * @param {number} scale px per unit, the renderer's current zoom
 * @param {number} [minPx] target floor; defaults to SHELF_GRAB_MIN_PX
 * @returns {Map<number, {fromUnits:number, toUnits:number}>} keyed by elementIndex
 */
export function shelfGrabBands(marks, scale, minPx = SHELF_GRAB_MIN_PX) {
  const bands = new Map();
  if (!(scale > 0)) return bands;
  const boards = boardsFrom(marks);
  const minUnits = minPx / scale;
  boards.forEach((board, i) => {
    // A group member is not a target, and neither is a LOCKED shelf — but both
    // are real boards, so they still bound what their neighbours may claim.
    if (board.elementIndex === null || board.locked) return;
    const above = boards[i + 1] ?? null;
    const below = boards[i - 1] ?? null;
    // Half the growth each side, so the board stays centred in its own target.
    const wanted = Math.max(0, (minUnits - (board.top - board.bottom)) / 2);
    const roomUp = above ? Math.max(0, (above.bottom - board.top) / 2) : Infinity;
    const roomDown = below ? Math.max(0, (board.bottom - below.top) / 2) : Infinity;
    bands.set(board.elementIndex, {
      fromUnits: board.bottom - Math.min(wanted, roomDown),
      toUnits: board.top + Math.min(wanted, roomUp),
    });
  });
  return bands;
}

/**
 * How far each draggable shelf may travel — the owner's ruling 8, verbatim:
 * *"it is OK to have the shelf constrained by the shelves above/below it."*
 *
 * ## Why constrain rather than pass through
 *
 * The element list is ordered top→bottom and THAT ORDER IS MEANING (see
 * solveInterior's pin segmentation). Dragging a board *through* its neighbour
 * would not move a coordinate, it would REORDER the list — a separate edit
 * that already has its own non-drag control in the editor's ▲▼ buttons
 * (`draftMove`). Constraining is not the cheap approximation here; it is the
 * behaviour that keeps a drag meaning one thing.
 *
 * ## Why physical non-overlap is the whole rule
 *
 * Ruling 2 allows shelves ONE SYSTEM HOLE apart, and that minimum needs no
 * separate term, because it falls out of non-overlap plus the snap the solver
 * already performs. A board is at least ¾" thick and the pitch is 32 mm
 * (≈1.26"), so a request clamped to "not overlapping the board below" always
 * sits more than halfway to the next hole up and therefore rounds AWAY from
 * the occupied one. Two boards can end up on ADJACENT holes — which is exactly
 * what the owner asked for — but never on the SAME hole. Pinned in
 * shelf-drag-targets.test.js; if the pitch or the thickness rules ever change
 * enough to break that margin, that test is what says so.
 *
 * Bounds are stated for the shelf's TOP SURFACE, because that is what
 * `atUnits` means and what `draftSetShelfPin` takes.
 *
 * @param {Array<object>} marks the output of interiorMarks()
 * @param {{bottomUnits:number, topUnits:number}} band the clear interior band
 * @returns {Map<number, {minTopUnits:number, maxTopUnits:number}>} by elementIndex
 */
export function shelfDragBounds(marks, band = {}) {
  const bounds = new Map();
  const boards = boardsFrom(marks);
  const floor = Number.isFinite(band.bottomUnits) ? band.bottomUnits : -Infinity;
  const ceiling = Number.isFinite(band.topUnits) ? band.topUnits : Infinity;

  boards.forEach((board, i) => {
    // Neither a group member nor a LOCKED shelf may move; both still constrain.
    if (board.elementIndex === null || board.locked) return;
    const above = boards[i + 1] ?? null;
    const below = boards[i - 1] ?? null;
    const thickness = Math.max(0, board.top - board.bottom);
    bounds.set(board.elementIndex, {
      // Its own thickness is why the floor is not simply the band bottom: a
      // board resting ON the floor still has its TOP a board-thickness up.
      minTopUnits: (below ? below.top : floor) + thickness,
      maxTopUnits: above ? above.bottom : ceiling,
    });
  });
  return bounds;
}

/**
 * Clamp a requested top-surface elevation into a shelf's legal travel.
 *
 * Deliberately does NOT snap. The solver already snaps every pin onto the hole
 * ladder (`solveInterior`'s seeding pass), and it is the only implementation
 * that knows about "required holes only" mode, where the designer's tape is law
 * and there is no ladder to snap to. A second snapper here could disagree with
 * that one, and a drag that lands somewhere the solver would not is a bug the
 * designer sees and we do not. So the drag states an intent and the model
 * decides where wood can actually go.
 *
 * Returns null when the shelf has no bounds (not draggable, or a range the band
 * cannot hold) — the caller then leaves the shelf alone, which is the same
 * "never silently move it" policy the pins already follow.
 *
 * @returns {number|null} the elevation to hand draftSetShelfPin
 */
export function clampShelfDrag(bounds, requestedTopUnits) {
  if (!bounds || !Number.isFinite(requestedTopUnits)) return null;
  const { minTopUnits, maxTopUnits } = bounds;
  if (!Number.isFinite(minTopUnits) || !Number.isFinite(maxTopUnits)) return null;
  if (maxTopUnits < minTopUnits) return null; // no room at all — leave it be
  return Math.min(Math.max(requestedTopUnits, minTopUnits), maxTopUnits);
}
